import React, { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Umbrella, Stethoscope, User as UserIcon, PartyPopper, HeartCrack, Scale, Plus, Minus, History } from "lucide-react";

/**
 * Admin dialog to manage all six benefit types for a single employee.
 *
 * Rendered inside AdminEmployees.jsx. Only opens when the tenant has
 * `benefits_enabled` on. Each benefit type has:
 *   - enable toggle
 *   - accrual method dropdown (annual grant / anniversary / per hr / per period / manual)
 *   - accrual rate input (units depend on method)
 *   - current balance display
 *   - +/− quick-adjust buttons that write a ledger entry
 *
 * The ledger is available under an "Adjustment history" collapsible.
 */

const BENEFIT_TYPES = [
  { key: "vacation",    label: "Paid Vacation",         Icon: Umbrella,      unitHint: "hours" },
  { key: "sick",        label: "Paid Sick",             Icon: Stethoscope,   unitHint: "hours" },
  { key: "personal",    label: "Personal Days",         Icon: UserIcon,      unitHint: "hours" },
  { key: "holiday",     label: "Paid Company Holidays", Icon: PartyPopper,   unitHint: "hours" },
  { key: "bereavement", label: "Bereavement",           Icon: HeartCrack,    unitHint: "hours" },
  { key: "jury",        label: "Jury Duty",             Icon: Scale,         unitHint: "hours" },
];

const ACCRUAL_METHODS = [
  { value: "annual_grant",              label: "Fixed annual grant (calendar year)", rateLabel: "Hours granted per year" },
  { value: "annual_grant_anniversary",  label: "Fixed annual grant (hire anniversary)", rateLabel: "Hours granted per year" },
  { value: "per_hour_worked",           label: "Accrual per hour worked", rateLabel: "Hours PTO per 1 hour worked (e.g. 0.025 = 1/40)" },
  { value: "per_pay_period",            label: "Accrual per pay period", rateLabel: "Hours accrued per paystub" },
  { value: "manual",                    label: "Manual (admin adjusts balance directly)", rateLabel: "N/A" },
];

const emptyCfg = () => ({
  enabled: false,
  accrual_method: "manual",
  accrual_rate: 0,
  balance_hours: 0,
  notes: "",
});

export default function BenefitsDialog({ open, employee, onOpenChange, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [adjust, setAdjust] = useState({ benefit_type: "", delta_hours: "", reason: "" });
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    if (!employee?.id) return;
    try {
      const [b, l] = await Promise.all([
        api.get(`/admin/employees/${employee.id}/benefits`),
        api.get(`/admin/employees/${employee.id}/pto/ledger`),
      ]);
      setData(b.data);
      setLedger(l.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [employee?.id]);

  useEffect(() => { if (open) { setShowHistory(false); load(); } }, [open, load]);

  if (!data) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" data-testid="benefits-dialog">
          <DialogHeader>
            <DialogTitle>Benefits — loading…</DialogTitle>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const setBenefit = (type, patch) => {
    setData((prev) => ({
      ...prev,
      benefits: { ...prev.benefits, [type]: { ...prev.benefits[type], ...patch } },
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const { key } of BENEFIT_TYPES) {
        const b = data.benefits[key] || emptyCfg();
        payload[key] = {
          enabled: !!b.enabled,
          accrual_method: b.accrual_method || "manual",
          accrual_rate: Number(b.accrual_rate) || 0,
          balance_hours: Number(b.balance_hours) || 0,
          notes: b.notes || null,
        };
      }
      await api.put(`/admin/employees/${employee.id}/benefits`, payload);
      toast.success("Benefits saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitAdjustment = async () => {
    if (!adjust.benefit_type || !adjust.delta_hours || !adjust.reason.trim()) {
      toast.error("Pick a benefit, an amount, and a reason.");
      return;
    }
    try {
      await api.post(`/admin/employees/${employee.id}/pto/adjust`, {
        benefit_type: adjust.benefit_type,
        delta_hours: Number(adjust.delta_hours),
        reason: adjust.reason.trim(),
      });
      toast.success("Balance adjusted");
      setAdjust({ benefit_type: "", delta_hours: "", reason: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const globalOn = data.benefits_enabled_globally;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="benefits-dialog">
        <DialogHeader>
          <DialogTitle>
            Benefits — {data.employee_name}
            <span className="ml-2 text-xs font-normal text-slate-500">
              @ {fmtMoney(data.hourly_rate)}/h
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure vacation, sick, personal, holiday, bereavement, and jury-duty benefits and current balances for this employee.
          </DialogDescription>
        </DialogHeader>

        {!globalOn && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
            Benefits are turned <b>off</b> globally for this company. Turn it on under Admin → Settings → Benefits &amp; PTO
            before employees can see their balances.
          </div>
        )}

        <div className="space-y-3">
          {BENEFIT_TYPES.map(({ key, label, Icon, unitHint }) => {
            const cfg = data.benefits[key] || emptyCfg();
            const methodMeta = ACCRUAL_METHODS.find((m) => m.value === (cfg.accrual_method || "manual"));
            return (
              <div
                key={key}
                className={`rounded-xl border p-3 transition-colors ${cfg.enabled ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-slate-50"}`}
                data-testid={`benefit-block-${key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${cfg.enabled ? "text-sky-700" : "text-slate-400"}`} />
                    <span className="font-bold text-slate-900">{label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Balance</span>
                    <span className="tabular-nums font-bold text-slate-900 text-lg">
                      {Number(cfg.balance_hours || 0).toFixed(2)}h
                    </span>
                    <Switch
                      checked={!!cfg.enabled}
                      onCheckedChange={(v) => setBenefit(key, { enabled: v })}
                      data-testid={`benefit-toggle-${key}`}
                    />
                  </div>
                </div>
                {cfg.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Accrual Method</Label>
                      <Select
                        value={cfg.accrual_method || "manual"}
                        onValueChange={(v) => setBenefit(key, { accrual_method: v })}
                      >
                        <SelectTrigger className="mt-1" data-testid={`benefit-method-${key}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACCRUAL_METHODS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {cfg.accrual_method !== "manual" && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">
                          {methodMeta?.rateLabel || "Accrual rate"}
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={cfg.accrual_rate ?? 0}
                          onChange={(e) => setBenefit(key, { accrual_rate: e.target.value })}
                          className="mt-1"
                          data-testid={`benefit-rate-${key}`}
                        />
                      </div>
                    )}
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Current Balance ({unitHint})</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={cfg.balance_hours ?? 0}
                        onChange={(e) => setBenefit(key, { balance_hours: e.target.value })}
                        className="mt-1"
                        data-testid={`benefit-balance-${key}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Note (optional)</Label>
                      <Input
                        placeholder="e.g. Prorated for 2026, resets Jan 1"
                        value={cfg.notes || ""}
                        onChange={(e) => setBenefit(key, { notes: e.target.value })}
                        className="mt-1"
                        data-testid={`benefit-notes-${key}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Quick +/- adjustment */}
        <div className="rounded-xl border border-slate-300 bg-white p-3">
          <p className="text-sm font-bold text-slate-900 mb-2">Quick balance adjustment</p>
          <p className="text-xs text-slate-500 mb-2">
            Adds a signed ledger entry — positive to credit, negative to deduct. Balance is updated instantly.
          </p>
          <div className="grid grid-cols-[1fr_100px_1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Benefit</Label>
              <Select
                value={adjust.benefit_type}
                onValueChange={(v) => setAdjust((a) => ({ ...a, benefit_type: v }))}
              >
                <SelectTrigger className="mt-1" data-testid="adjust-benefit-select">
                  <SelectValue placeholder="Pick a type…" />
                </SelectTrigger>
                <SelectContent>
                  {BENEFIT_TYPES.map((b) => (
                    <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">± Hours</Label>
              <Input
                type="number"
                step="0.25"
                placeholder="+8 or -4"
                value={adjust.delta_hours}
                onChange={(e) => setAdjust((a) => ({ ...a, delta_hours: e.target.value }))}
                className="mt-1"
                data-testid="adjust-delta-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Reason</Label>
              <Input
                placeholder="e.g. Used July 4"
                value={adjust.reason}
                onChange={(e) => setAdjust((a) => ({ ...a, reason: e.target.value }))}
                className="mt-1"
                data-testid="adjust-reason-input"
              />
            </div>
            <Button onClick={submitAdjustment} className="bg-sky-600 hover:bg-sky-700" data-testid="adjust-submit-btn">
              {Number(adjust.delta_hours) < 0 ? <Minus className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Apply
            </Button>
          </div>
        </div>

        {/* Ledger history */}
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="w-full flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          data-testid="benefits-history-toggle"
        >
          <span className="flex items-center gap-1.5"><History className="h-4 w-4" /> Adjustment history ({ledger.length})</span>
          <span>{showHistory ? "▲" : "▼"}</span>
        </button>
        {showHistory && (
          <div className="rounded-xl border border-slate-200 overflow-hidden text-xs" data-testid="benefits-ledger">
            {ledger.length === 0 ? (
              <div className="px-3 py-3 text-slate-500">No adjustments yet.</div>
            ) : (
              <>
                <div className="grid grid-cols-[100px_1fr_60px_70px_70px_1fr] gap-2 bg-slate-50 px-3 py-1.5 font-bold uppercase tracking-widest text-[10px] text-slate-500">
                  <span>Date</span><span>Type</span><span className="text-right">Δ</span><span className="text-right">Before</span><span className="text-right">After</span><span>Reason</span>
                </div>
                {ledger.map((r) => (
                  <div key={r.id} className="grid grid-cols-[100px_1fr_60px_70px_70px_1fr] gap-2 px-3 py-1.5 border-t border-slate-100 tabular-nums">
                    <span className="text-slate-700">{r.at ? fmtDate(r.at.slice(0, 10)) : ""}</span>
                    <span className="text-slate-700 capitalize">{r.benefit_type}</span>
                    <span className={`text-right font-bold ${Number(r.delta_hours) < 0 ? "text-red-700" : "text-emerald-700"}`}>
                      {Number(r.delta_hours) > 0 ? "+" : ""}{Number(r.delta_hours).toFixed(2)}
                    </span>
                    <span className="text-right">{Number(r.balance_before).toFixed(2)}</span>
                    <span className="text-right">{Number(r.balance_after).toFixed(2)}</span>
                    <span className="text-slate-600 truncate" title={r.reason}>{r.reason}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Cancel</Button>
          <Button onClick={save} disabled={busy} className="flex-1 bg-sky-600 hover:bg-sky-700" data-testid="benefits-save-btn">
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
